import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import { COPY } from './copy';
import { useOwnerQa } from '../contexts/OwnerQaContext';
import { usePro } from '../contexts/ProContext';
import ResponsiveAppMenu from '../components/ResponsiveAppMenu';
import { checkoutRequestId, clearCheckoutRequestIds, sessionPath, trainingRequest } from './api';
import './training-payments.css';

const DISPLAY_PLANS = [{ id: 'random', amount: 70 }, { id: 'personalised', amount: 90 }, { id: 'hood', amount: 150 }, { id: 'lichess', amount: 299 }];
const SYMBOLS = { random: '♟', personalised: '✦', hood: '♞', lichess: '↗' };

const STORY = {
  en: {
    eyebrow: 'MEET YOUR CHESS COACH',
    title: 'Your next good move starts here.',
    intro: 'Solve a position. Hood spots what to work on. You get better, move by move.',
    bubble: 'Show me one move. We will find your next challenge together!',
    steps: ['Play', 'Discover', 'Improve'],
    free: 'Meet Hood — try free',
    freeNote: 'See Hood in action before choosing a paid session.',
    selected: 'YOUR NEXT TRAINING',
    priceNote: 'One payment · 30 minutes of training',
    other: 'See other training options',
    less: 'Hide other options',
    otherHint: 'Choose another session below',
    proBadge: 'FULL TRAINING ACCESS',
    proDesc: 'Keep training with Hood, without buying a separate session each time.',
    perMonth: '/ month',
    proButton: 'Explore Chesshood PRO',
    proLegal: 'Monthly plan renews automatically. Select Monthly at checkout.',
    qa: 'Owner test mode · no money charged',
    qaStart: 'Test this session',
    qaFirst: 'Test first visit with Hood',
    qaSettings: 'QA settings',
    info: 'Session details & payment information',
    visualHint: 'Your move. Your plan. Your coach.',
  },
  pl: {
    eyebrow: 'POZNAJ SWOJEGO TRENERA',
    title: 'Twój kolejny dobry ruch zaczyna się tutaj.',
    intro: 'Rozwiąż pozycję. Hood znajdzie słabszy motyw. Ćwicz krok po kroku.',
    bubble: 'Pokaż mi jeden ruch. Razem znajdziemy kolejne wyzwanie!',
    steps: ['Zagraj', 'Odkryj', 'Popraw się'],
    free: 'Poznaj Hooda — spróbuj za darmo',
    freeNote: 'Zobacz Hooda w działaniu, zanim wybierzesz płatny trening.',
    selected: 'TWÓJ NASTĘPNY TRENING',
    priceNote: 'Jedna płatność · 30 minut treningu',
    other: 'Zobacz inne treningi',
    less: 'Ukryj pozostałe treningi',
    otherHint: 'Wybierz inny trening poniżej',
    proBadge: 'PEŁNY DOSTĘP DO TRENINGU',
    proDesc: 'Trenuj z Hoodem bez kupowania kolejnej osobnej sesji.',
    perMonth: '/ miesiąc',
    proButton: 'Poznaj Chesshood PRO',
    proLegal: 'Abonament odnawia się co miesiąc. Wybierz plan miesięczny przy płatności.',
    qa: 'Test właściciela · bez pobierania pieniędzy',
    qaStart: 'Testuj tę sesję',
    qaFirst: 'Testuj pierwszą wizytę z Hoodem',
    qaSettings: 'Ustawienia testów',
    info: 'Szczegóły sesji i płatności',
    visualHint: 'Twój ruch. Twój plan. Twój trener.',
  },
  es: {
    eyebrow: 'CONOCE A TU ENTRENADOR',
    title: 'Tu próxima buena jugada empieza aquí.',
    intro: 'Resuelve una posición. Hood descubre qué debes mejorar. Avanza paso a paso.',
    bubble: 'Enséñame una jugada. ¡Busquemos juntos tu próximo reto!',
    steps: ['Juega', 'Descubre', 'Mejora'],
    free: 'Conoce a Hood — pruébalo gratis',
    freeNote: 'Prueba Hood antes de elegir una sesión de pago.',
    selected: 'TU PRÓXIMO ENTRENAMIENTO',
    priceNote: 'Un pago · 30 minutos de entrenamiento',
    other: 'Ver otros entrenamientos',
    less: 'Ocultar otras opciones',
    otherHint: 'Elige otra sesión a continuación',
    proBadge: 'ACCESO COMPLETO AL ENTRENAMIENTO',
    proDesc: 'Entrena con Hood sin comprar cada sesión por separado.',
    perMonth: '/ mes',
    proButton: 'Descubre Chesshood PRO',
    proLegal: 'Renovación mensual automática. Selecciona Mensual al pagar.',
    qa: 'Modo de prueba · sin cargos',
    qaStart: 'Probar esta sesión',
    qaFirst: 'Probar la primera visita con Hood',
    qaSettings: 'Ajustes de prueba',
    info: 'Detalles de sesión y pago',
    visualHint: 'Tu jugada. Tu plan. Tu entrenador.',
  },
};

export default function TrainingShop() {
  const { firebaseUser, loading: authLoading } = useContext(UserContext);
  const { language } = useLanguage();
  const qa = useOwnerQa();
  const { isPro: subscriptionIsPro } = usePro();
  const text = COPY[language] || COPY.en;
  const location = useLocation();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(false);
  const [selectedMode, setSelectedMode] = useState('personalised');
  const [showOther, setShowOther] = useState(false);
  const checkout = new URLSearchParams(location.search).get('checkout');
  const cancelled = new URLSearchParams(location.search).has('cancelled');
  const requestedMode = new URLSearchParams(location.search).get('mode') || location.state?.mode || '';
  useEffect(() => {
    if (DISPLAY_PLANS.some(plan => plan.id === requestedMode)) setSelectedMode(requestedMode);
  }, [requestedMode]);


  useEffect(() => {
    // QA purchases are simulated entirely in the owner context. A missing
    // production billing endpoint must not break previewing a training pass.
    if (qa.active) {
      setCatalog({ plans: DISPLAY_PLANS, paymentsAvailable: true, benefits: {} });
      setError(false);
      return;
    }
    const controller = new AbortController();
    trainingRequest('catalog', { signal: controller.signal }).then(setCatalog).catch(e => {
      if (e.name !== 'AbortError') setError(true);
    });
    return () => controller.abort();
  }, [firebaseUser?.uid, qa.active]);

  const refresh = useCallback(async (signal) => {
    if (!firebaseUser || qa.active) { setLoading(false); setError(false); return; }
    setLoading(true); setError(false);
    try {
      const data = await trainingRequest(`sessions${checkout ? `?checkout=${encodeURIComponent(checkout)}` : ''}`, { signal });
      setAccount(data);
      if (checkout && !data.paymentPending) clearCheckoutRequestIds(firebaseUser.uid);
    } catch (e) { if (e.name !== 'AbortError') setError(true); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [firebaseUser, checkout, qa.active]);

  useEffect(() => {
    setAccount(null);
    const controller = new AbortController();
    if (firebaseUser) refresh(controller.signal);
    return () => controller.abort();
  }, [firebaseUser, refresh]);

  const trainingDestination = (mode, id = '') => {
    try {
      if (['hood', 'lichess'].includes(mode) && sessionStorage.getItem('chesshood:returnToAnalysis')) {
        sessionStorage.removeItem('chesshood:returnToAnalysis');
        return '/analysis' + (id ? '?session=' + encodeURIComponent(id) : '');
      }
    } catch {}
    if (qa.active && ['hood', 'lichess'].includes(mode)) {
      return '/hood-ai-beta' + (id ? '?session=' + encodeURIComponent(id) : '');
    }
    return sessionPath(mode, id);
  };

  const openSession = async (session) => {
    setBusy(session.id); setError(false);
    try {
      if (qa.active && session.qa) {
        const active = session.state === 'ready' ? qa.startPass(session.mode) : session;
        if (!active || active.state !== 'active') return;
        const destination = trainingDestination(active.mode).split('?')[0];
        navigate(`${destination}?qaSession=${encodeURIComponent(active.mode)}`);
        return;
      }
      const data = session.state === 'ready'
        ? await trainingRequest('start', { body: { sessionId: session.id } }) : { session };
      if (data.session.state !== 'active') { await refresh(); return; }
      navigate(trainingDestination(data.session.mode, data.session.id));
    } catch { setError(true); }
    finally { setBusy(''); }
  };

  const buy = async (mode) => {
    if (!firebaseUser) {
      navigate('/login', { state: { from: { pathname: '/training', search: location.search } } });
      return;
    }
    if (qa.active) {
      if (subscriptionIsPro) { navigate(trainingDestination(mode)); return; }
      qa.purchasePass(mode);
      setBusy('');
      return;
    }
    if (account?.isPro) { navigate(trainingDestination(mode)); return; }
    setBusy(mode); setError(false);
    try {
      const result = await trainingRequest('checkout', { body: { mode, requestId: checkoutRequestId(firebaseUser.uid, mode) } });
      const url = new URL(result.url);
      if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com') throw new Error('invalid_checkout');
      window.location.assign(url.href);
    } catch (e) {
      if (['checkout_finished', 'request_expired'].includes(e.message)) {
        clearCheckoutRequestIds(firebaseUser.uid);
        await refresh();
      }
      setError(true); setBusy('');
    }
  };
  const qaAvailable = DISPLAY_PLANS
    .map(({ id }) => qa.getPass(id))
    .filter((session) => session && ['ready', 'active'].includes(session.state));
  const available = qa.active
    ? qaAvailable
    : (account?.sessions || []).filter(s => ['ready', 'active'].includes(s.state));
  const effectiveIsPro = qa.active ? subscriptionIsPro : Boolean(account?.isPro);


  const allPlans = (catalog?.plans || DISPLAY_PLANS).filter(plan => Boolean(text.modes[plan.id]));
  const selectedPlan = allPlans.find(plan => plan.id === selectedMode) || allPlans[0] || DISPLAY_PLANS[1];
  const selectedCopy = text.modes[selectedPlan.id] || text.modes.personalised;
  const selectedSaved = available.find(session => session.mode === selectedPlan.id);
  const story = STORY[language] || STORY.en;
  const selectedPrice = '£' + (selectedPlan.amount / 100).toFixed(2);
  const proMonthlyPrice = process.env.REACT_APP_STRIPE_MONTHLY_DISPLAY || '£2.99';
  const purchaseDisabled = (!catalog && !qa.active) || Boolean(busy) || authLoading
    || (!qa.active && firebaseUser && (loading || !account))
    || (!qa.active && !catalog?.paymentsAvailable && !selectedSaved && !effectiveIsPro);
  const selectedAction = busy === selectedPlan.id ? text.preparing
    : selectedSaved ? (selectedSaved.state === 'ready' ? text.start : text.resume)
    : effectiveIsPro ? text.pro : qa.active ? story.qaStart : text.buy;

  const previewFirstVisit = () => {
    if (!qa.active || !qa.isOwner) return;
    // A fresh visit is a QA-only simulation. It never modifies the real account.
    qa.setAccountAgeDays(0);
    qa.setSubscriptionMode('free');
    navigate('/hood-ai-alpha');
  };

  return <main className="training-shop">
    <div className="training-shop-inner">
      <nav className="training-shop-nav" aria-label="Training navigation">
        <Link to="/" className="training-brand">♞ <span>Chesshood</span></Link>
        <ResponsiveAppMenu />
      </nav>

      <header className="training-story">
        <div className="training-story-copy">
          <p className="training-eyebrow">{story.eyebrow}</p>
          <h1>{story.title}</h1>
          <p className="training-intro">{story.intro}</p>
          <div className="training-journey" aria-label={story.visualHint}>
            {story.steps.map((step, index) => <div className="training-journey-step" key={step}>
              <span aria-hidden="true">{['♟', '✦', '♜'][index]}</span>
              <strong>{step}</strong>
            </div>)}
          </div>
          <Link className="training-free-link" to={firebaseUser ? '/hood-ai-alpha' : '/play'}>
            {story.free} <span aria-hidden="true">→</span>
          </Link>
          <p className="training-free-note">{story.freeNote}</p>
        </div>
        <div className="training-hood-visual">
          <div className="training-hood-scene">
            <span className="training-floating-piece training-floating-piece-one" aria-hidden="true">♙</span>
            <span className="training-floating-piece training-floating-piece-two" aria-hidden="true">♘</span>
            <span className="training-floating-piece training-floating-piece-three" aria-hidden="true">♜</span>
            <img src="/hood.png" alt="Hood, your Chesshood chess coach" className="training-hood-mascot" />
          </div>
          <div className="training-hood-bubble">
            <span className="training-hood-bubble-dot" aria-hidden="true">●</span>
            {story.bubble}
          </div>
        </div>
      </header>

      {catalog?.benefits?.complimentary && <p className="training-notice">HCA complimentary access is active.</p>}
      {!!catalog?.benefits?.discountPercent && <p className="training-notice">{catalog.benefits.reason}: {catalog.benefits.discountPercent}% off. Prices shown include your discount.</p>}
      {qa.active && <div className="training-qa-tools" role="status">
        <span className="training-qa-label">● {story.qa}</span>
        {qa.isOwner && <>
          <button type="button" onClick={previewFirstVisit}>{story.qaFirst} ↗</button>
          <Link to="/owner-qa">{story.qaSettings}</Link>
        </>}
      </div>}
      {(authLoading || loading) && <p role="status" className="training-notice">{text.checking}</p>}
      {cancelled && <p className="training-notice" role="status">{text.cancel}</p>}
      {checkout && !firebaseUser && !authLoading && <p className="training-notice"><Link to="/login" state={{ from: location }}>{text.login}</Link></p>}
      {!qa.active && account?.paymentPending && <p className="training-notice" role="status">{text.pending} <button type="button" onClick={() => refresh()} disabled={loading}>{text.refresh}</button></p>}
      {effectiveIsPro && <p className="training-notice">{qa.active ? 'QA: ' + qa.config.subscriptionMode.toUpperCase() + ' access' : text.included}</p>}
      {error && <div className="training-error" role="alert">{text.error} <button type="button" onClick={() => window.location.reload()}>{text.refresh}</button></div>}
      {catalog && !catalog.paymentsAvailable && <p className="training-error" role="status">{text.unavailable}</p>}

      {available.length > 0 && <section className="training-owned" aria-label={text.yours}>
        <h2>{text.yours}</h2>
        {available.map(session => <div className="training-owned-row" key={session.id}>
          <div><strong>{text.modes[session.mode]?.[0]}</strong><span>{session.state === 'ready' ? text.ready : text.active}</span></div>
          <button type="button" onClick={() => openSession(session)} disabled={Boolean(busy)}>
            {session.state === 'ready' ? text.start : text.resume} <span aria-hidden="true">→</span>
          </button>
        </div>)}
      </section>}

      <section className="training-choice" aria-labelledby="training-choice-title">
        <div className="training-choice-heading">
          <span className="training-section-eyebrow">{story.selected}</span>
          <h2 id="training-choice-title">{selectedCopy[0]}</h2>
          <p>{selectedCopy[1]}</p>
        </div>
        <div className="training-choice-body">
          <div className="training-choice-benefits">
            {selectedCopy.slice(2, 4).map((item, index) =>
              <span key={item}><span aria-hidden="true">{index === 0 ? '♟' : '✦'}</span>{item}</span>
            )}
          </div>
          <div className="training-choice-bottom">
            <div className="training-choice-price" aria-label={story.priceNote}>
              <div className="training-price">
                {selectedPlan.regularAmount > selectedPlan.amount && <del>£{(selectedPlan.regularAmount / 100).toFixed(2)}</del>}
                {selectedPrice}
              </div>
              <p className="training-once">{story.priceNote}</p>
            </div>
            <button type="button" className="training-main-cta" disabled={purchaseDisabled}
              onClick={() => selectedSaved ? openSession(selectedSaved) : buy(selectedPlan.id)}>
              {selectedAction} <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
        <p className="training-choice-small">✓ {text.noSub}</p>
      </section>

      <section className="training-more-shell">
        <button type="button" className="training-more-trigger" aria-expanded={showOther}
          aria-controls="training-more-list" onClick={() => setShowOther(value => !value)}>
          <span><strong>{showOther ? story.less : story.other}</strong><small>{story.otherHint}</small></span>
          <span aria-hidden="true" className={showOther ? 'training-more-arrow training-more-arrow-up' : 'training-more-arrow'}>⌄</span>
        </button>
        <div className="training-extra-plans" id="training-more-list" hidden={!showOther}>
          {allPlans.filter(plan => plan.id !== selectedPlan.id).map(plan =>
            <button type="button" key={plan.id} className="training-extra-option"
              onClick={() => { setSelectedMode(plan.id); setShowOther(false); }}
              aria-label={text.modes[plan.id][0] + ' — £' + (plan.amount / 100).toFixed(2)}>
              <span className="training-extra-symbol" aria-hidden="true">{SYMBOLS[plan.id]}</span>
              <span className="training-extra-title">{text.modes[plan.id][0]}<small>{text.modes[plan.id][1]}</small></span>
              <strong>£{(plan.amount / 100).toFixed(2)}</strong>
              <span aria-hidden="true">→</span>
            </button>)}
        </div>
      </section>

      <section className="training-pro-strip" aria-label="Chesshood Pro">
        <div className="training-pro-badge" aria-hidden="true">♛</div>
        <div className="training-pro-description">
          <span className="training-pro-eyebrow">{story.proBadge}</span>
          <h2>Chesshood PRO</h2>
          <p>{story.proDesc}</p>
          <small>{story.proLegal}</small>
        </div>
        <div className="training-pro-actions">
          <div className="training-pro-price"><strong>{proMonthlyPrice}</strong><span>{story.perMonth}</span></div>
          <Link to="/checkout" className="training-pro-link">{story.proButton} <span aria-hidden="true">→</span></Link>
        </div>
      </section>

      {!catalog && !qa.active && !error && <p role="status" className="training-shop-checking">{text.checking}</p>}
      <footer className="training-shop-footer">
        <details>
          <summary>{story.info}</summary>
          <p>{text.timer}</p>
          <p>{text.limit}</p>
          <p>{text.security}</p>
        </details>
        <Link to="/analysis">{language === 'pl' ? 'Przeanalizuj pierwsze 4 partie za darmo →'
          : language === 'es' ? 'Analiza tus primeras 4 partidas gratis →'
            : 'Analyse your first 4 games free →'}</Link>
      </footer>
    </div>
  </main>;
}
